import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface TTSOptions {
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  speed?: number; // 0.25 to 4.0
  model?: 'tts-1' | 'tts-1-hd';
}

@Injectable({
  providedIn: 'root'
})
export class TTSService {
  private currentAudio: HTMLAudioElement | null = null;
  private apiUrl = `${environment.apiUrl}/ai`;

  constructor(private http: HttpClient) {}

  /**
   * Generate speech using OpenAI TTS via backend
   * @param text The text to speak
   * @param options TTS options
   */
  async speak(text: string, options: TTSOptions = {}): Promise<void> {
    try {
      // Stop any current speech
      this.stop();

      const ttsOptions = {
        text,
        voice: options.voice || 'nova',
        speed: options.speed || 1.0,
        model: options.model || 'tts-1'
      };

      // Request audio as blob for streaming
      const response = await this.http.post(
        `${this.apiUrl}/text-to-speech/sync`, 
        ttsOptions,
        { responseType: 'blob' }
      ).toPromise();

      if (response) {
        // Create audio URL from blob for immediate playback
        const audioBlob = new Blob([response], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        this.currentAudio = new Audio(audioUrl);
        
        return new Promise((resolve, reject) => {
          this.currentAudio!.onended = () => {
            URL.revokeObjectURL(audioUrl); // Clean up blob URL
            this.currentAudio = null;
            resolve();
          };
          
          this.currentAudio!.onerror = () => {
            URL.revokeObjectURL(audioUrl); // Clean up blob URL
            this.currentAudio = null;
            reject(new Error('Failed to play audio'));
          };
          
          this.currentAudio!.play().catch(reject);
        });
      }
    } catch (error) {
      throw new Error(`TTS generation failed: ${error}`);
    }
  }

  /**
   * Stop current speech
   */
  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  /**
   * Check if TTS is supported (always true since using backend)
   */
  isSupported(): boolean {
    return true;
  }

  /**
   * Speak AI description with optimized settings for narration
   * Can accept either text description or existing audio URL
   */
  speakAIDescription(description: string, existingAudioUrl?: string): Promise<void> {
    // If we have an existing audio URL, use it directly
    if (existingAudioUrl && existingAudioUrl.trim()) {
      return this.playAudioFromUrl(existingAudioUrl);
    }
    
    // Otherwise generate new TTS from text
    return this.speak(description, {
      voice: 'nova', // Clear, balanced voice
      speed: 0.9, // Slightly slower for better comprehension
      model: 'tts-1-hd' // Higher quality model
    });
  }

  /**
   * Play audio from existing URL (cached/pre-generated)
   */
  async playAudioFromUrl(audioUrl: string): Promise<void> {
    try {
      // Stop any current speech
      this.stop();

      // Handle both relative and full URLs
      const fullAudioUrl = audioUrl.startsWith('http') 
        ? audioUrl 
        : `${environment.apiUrl}${audioUrl}`;

      this.currentAudio = new Audio(fullAudioUrl);
      
      return new Promise((resolve, reject) => {
        this.currentAudio!.onended = () => {
          this.currentAudio = null;
          resolve();
        };
        
        this.currentAudio!.onerror = () => {
          this.currentAudio = null;
          reject(new Error('Failed to play audio'));
        };
        
        this.currentAudio!.play().catch(reject);
      });
    } catch (error) {
      throw new Error(`Audio playback failed: ${error}`);
    }
  }
}